import { Check, X, Sparkles, Clock, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Badge } from "@/components/ui/badge";

export const ComparisonTable = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    {
      name: "Documentation speed",
      youxforma: "30 seconds",
      traditional: "5-8 hours",
      zeplin: "1-2 hours",
    },
    {
      name: "AI-powered",
      youxforma: true,
      traditional: false,
      zeplin: false,
    },
    {
      name: "Auto-updates with design changes",
      youxforma: true,
      traditional: false,
      zeplin: false,
    },
    {
      name: "Design system analysis",
      youxforma: true,
      traditional: false,
      zeplin: false,
    },
    {
      name: "Developer code snippets",
      youxforma: true,
      traditional: false,
      zeplin: true,
    },
    {
      name: "Pricing",
      youxforma: "$15/mo",
      traditional: "Free (your time)",
      zeplin: "$12/mo",
    },
  ];

  const renderCell = (value: string | boolean) => {
    if (typeof value === "boolean") {
      return value ? (
        <Check className="h-5 w-5 text-green-500 mx-auto" />
      ) : (
        <X className="h-5 w-5 text-muted-foreground/30 mx-auto" />
      );
    }
    return <span className="font-medium">{value}</span>;
  };

  const products = [
    {
      name: "youXforma",
      icon: Sparkles,
      featured: true,
    },
    {
      name: "Traditional",
      icon: Clock,
      featured: false,
    },
    {
      name: "Zeplin",
      icon: Zap,
      featured: false,
    },
  ];

  return (
    <section className="section-padding bg-gradient-to-b from-background via-muted/10 to-background" ref={ref}>
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <Badge variant="secondary" className="mb-4 glass-card">
            <Sparkles className="mr-2 h-3 w-3" />
            Comparison
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Why Choose <span className="gradient-text">youXforma?</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            See how we stack up against traditional handoffs and other tools
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-5xl mx-auto"
        >
          {/* Modern Card Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Feature Names Column */}
            <div className="space-y-4">
              <div className="glass-card p-4 h-24 flex items-center justify-center">
                <h3 className="font-bold text-muted-foreground text-sm">Features</h3>
              </div>
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="glass-card p-4 h-20 flex items-center hover-glow transition-all"
                >
                  <span className="text-sm font-medium">{feature.name}</span>
                </div>
              ))}
            </div>

            {/* Product Columns */}
            {products.map((product, productIndex) => {
              const Icon = product.icon;
              return (
                <div key={productIndex} className="space-y-4">
                  {/* Product Header */}
                  <div
                    className={`glass-card p-4 h-24 flex flex-col items-center justify-center gap-2 relative overflow-hidden ${
                      product.featured
                        ? "bg-gradient-to-br from-primary/20 to-secondary/10 border-primary/50 shadow-lg hover-lift"
                        : ""
                    }`}
                  >
                    {product.featured && (
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-50" />
                    )}
                    <Icon
                      className={`h-6 w-6 relative z-10 ${
                        product.featured ? "text-primary" : "text-muted-foreground"
                      }`}
                    />
                    <h3
                      className={`font-bold text-base relative z-10 ${
                        product.featured ? "gradient-text text-lg" : "text-muted-foreground"
                      }`}
                    >
                      {product.name}
                    </h3>
                    {product.featured && (
                      <Badge variant="default" className="text-xs px-3 py-0.5 relative z-10 bg-primary shadow-md">
                        ⭐ Best Choice
                      </Badge>
                    )}
                  </div>

                  {/* Product Features */}
                  {features.map((feature, index) => (
                    <div
                      key={index}
                      className={`glass-card p-4 h-20 flex items-center justify-center text-center transition-all ${
                        product.featured ? "hover-glow border-primary/20 bg-primary/5" : ""
                      }`}
                    >
                      <div
                        className={
                          product.featured ? "text-primary font-semibold" : "text-muted-foreground"
                        }
                      >
                        {renderCell(
                          feature[
                            product.name.toLowerCase() as
                              | "youxforma"
                              | "traditional"
                              | "zeplin"
                          ]
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <p className="text-sm text-muted-foreground">
            * Based on average time for typical design handoff scenarios
          </p>
        </motion.div>
      </div>
    </section>
  );
};